import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { toast } from "sonner";
import { Flower2 } from "lucide-react";

const EMPTY = {
  name: "", email: "", phone: "", location: "",
  tradition: "", contact: "", background: "", motivation: "",
};

const FIELDS = [
  { k: "name", label: "Full name", required: true, half: true },
  { k: "email", label: "Email", type: "email", required: true, half: true },
  { k: "phone", label: "Phone", half: true },
  { k: "location", label: "Region", placeholder: "Country / Nation / area you serve", half: true },
  { k: "tradition", label: "Tradition / lineage", required: true, placeholder: "e.g. Aboriginal & Torres Strait Islander, Ayurveda, TCM, Curanderismo" },
  { k: "contact", label: "How members can reach you (optional)", placeholder: "Website, email, or phone" },
  { k: "background", label: "Your practice & background", multiline: true, required: true, placeholder: "Your lineage, training, years of practice, the healing modalities you offer…" },
  { k: "motivation", label: "Why do you wish to be listed with Badi?", multiline: true, required: true },
];

export default function BecomeHealer() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.tradition || !form.background || !form.motivation) {
      toast.error("Please complete name, email, tradition, background and motivation.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/healer-applications", form);
      toast.success(data?.message || "Application received.");
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit application.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <Header />
      <section className="px-6 sm:px-12 lg:px-20 pt-20 pb-12 max-w-3xl mx-auto">
        <p className="label-eyebrow mb-3 flex items-center gap-2"><Flower2 className="w-3.5 h-3.5 text-[#7A4A6B]" /> A conduit for ancestral wisdom</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2D322B]">List as a Traditional Healer.</h1>
        <p className="mt-5 text-[#5C6359] leading-relaxed">
          Badi curates Indigenous and traditional healers as a respectful, one-stop point of contact
          for our members. We honour your lineage and practise — share your background below and our
          team will be in touch.
        </p>
        <div className="mt-5 inline-flex items-center gap-2 text-xs text-[#5C6359] bg-[#EFE3EC] border border-[#D8C0D2] rounded-full px-4 py-2">
          <Flower2 className="w-3.5 h-3.5 text-[#7A4A6B]" /> Traditional healing practice — offered in cultural context; not a substitute for medical care.
        </div>
      </section>

      <section className="px-6 sm:px-12 lg:px-20 pb-24 max-w-3xl mx-auto">
        {done ? (
          <div className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-10 text-center" data-testid="healer-success">
            <Flower2 className="w-10 h-10 text-[#7A4A6B] mx-auto" />
            <h2 className="font-serif text-2xl text-[#2D322B] mt-4">Thank you.</h2>
            <p className="text-[#5C6359] mt-3">
              Your application is in. Our team will review it with care and reach out if it&apos;s a fit.
            </p>
            <button onClick={() => navigate("/practitioners")} className="btn-primary mt-6 !py-2 !px-6" data-testid="healer-back-btn">
              Back to our team
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-8" data-testid="healer-application-form">
            <div className="grid sm:grid-cols-2 gap-6">
              {FIELDS.map((f) => (
                <div key={f.k} className={f.half ? "" : "sm:col-span-2"}>
                  <label className="text-[10px] uppercase tracking-widest text-[#5C6359] block mb-1">
                    {f.label}{f.required && <span className="text-[#C05C42]"> *</span>}
                  </label>
                  {f.multiline ? (
                    <textarea
                      rows={4}
                      value={form[f.k]}
                      onChange={set(f.k)}
                      placeholder={f.placeholder || ""}
                      data-testid={`healer-field-${f.k}`}
                      className="w-full bg-transparent border border-[#D1C7B8] focus:border-[#C05C42] rounded-xl p-3 outline-none text-[#2D322B]"
                    />
                  ) : (
                    <input
                      type={f.type || "text"}
                      value={form[f.k]}
                      onChange={set(f.k)}
                      placeholder={f.placeholder || ""}
                      data-testid={`healer-field-${f.k}`}
                      className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B]"
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end pt-6">
              <button type="submit" disabled={submitting} className="btn-primary !py-2.5 !px-7" data-testid="healer-submit-btn">
                {submitting ? "Submitting…" : "Submit application"}
              </button>
            </div>
          </form>
        )}
      </section>
      <Footer />
    </div>
  );
}
